import React, { useState } from 'react';

import CloseIcon from '@material-ui/icons/Close';
import IconButton from '@material-ui/core/IconButton';
import ImageList from '@material-ui/core/ImageList';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import DialogPaper from '../DialogPaper';

import * as StampCampaignService from '../../api/stampCampaignService';
import FullScreenDialog, { FullScreenDialogProps } from '../FullScreenDialog';

const useCardStyles = makeStyles((theme) => ({
  cardContainer: ({ highlight }: { highlight: boolean }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderStyle: 'solid',
    borderColor: highlight
      ? theme.palette.primary.main
      : theme.palette.grey[400],
    backgroundColor: highlight
      ? theme.palette.primary.main
      : theme.palette.common.white,
    width: '100%',
    // minHeight (not height) so a card name that wraps to two or three lines
    // grows the button instead of overflowing its border.
    minHeight: 'clamp(52px, 10vh, 80px)',
    padding: 'clamp(6px, 1.5vh, 12px) clamp(10px, 3vw, 20px)',
    boxSizing: 'border-box',
    borderRadius: 5,
    cursor: 'pointer',
    '&:active': {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.primary.main,
    },
    '&:active $card': {
      color: theme.palette.common.white,
    },
  }),
  card: ({ highlight }: { highlight: boolean }) => ({
    color: highlight ? theme.palette.common.white : theme.palette.common.black,
    fontWeight: theme.typography.fontWeightBold,
    fontSize: 'clamp(16px, 3vh, 34px)',
    // Keeps every line of a wrapped card name centred; the parent flex box only
    // centres the text block as a whole.
    textAlign: 'center',
    lineHeight: 1.25,
  }),
}));

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    marginTop: theme.spacing(),
    marginBottom: 'clamp(8px, 3vh, 64px)',
  },
  tile: {
    marginBottom: theme.spacing(1),
  },
  cardContainer: {
    padding: theme.spacing(2),
    width: '100%',
  },
  title: {
    marginTop: 'clamp(8px, 2vh, 24px)',
    fontWeight: theme.typography.fontWeightBold,
    // Scales with the dialog height: large in tall (portrait) dialogs, smaller
    // in short (landscape) ones so the cards below always stay on-screen.
    fontSize: 'clamp(22px, 5vh, 44px)',
    textAlign: 'center',
    marginBottom: 'clamp(16px, 4vh, 48px)',
  },
  box: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
  },
  close: {
    marginLeft: 'auto',
  },
  closeIcon: {
    color: theme.palette.common.black,
  },
}));
//------------------------------------------------------------------------------
// Validation
//------------------------------------------------------------------------------

type Props = Pick<FullScreenDialogProps, 'open'> & {
  onClose: () => void;
  confirmSelectedSlotNumber: (selectedSlotNumber: number) => void;
  stampCampaignList: StampCampaignService.StampCampaign[];
  currentSelectedSlotNumber: number;
};

export default function SelectCardDialog({
  onClose,
  open,
  confirmSelectedSlotNumber,
  stampCampaignList,
  currentSelectedSlotNumber,
}: Props) {
  const [selectedSlotNumber, setSelectedSlotNumber] = useState(0);
  const classes = useStyles();

  function Card({
    value,
    highlight,
    handleClick,
  }: {
    value: string;
    highlight: boolean;
    handleClick: () => void;
  }) {
    const classes = useCardStyles({ highlight });

    return (
      <div className={classes.cardContainer} onClick={handleClick}>
        <Typography variant="h4" className={classes.card}>
          {value}
        </Typography>
      </div>
    );
  }

  const renderCards = () => {
    return stampCampaignList.map((stmp, idx) => (
      <div className={classes.tile} key={idx}>
        <Card
          value={stmp.name}
          highlight={stmp.slotNumber === selectedSlotNumber}
          handleClick={() => {
            setSelectedSlotNumber(stmp.slotNumber);
            confirmSelectedSlotNumber(stmp.slotNumber);
          }}
        />
      </div>
    ));
  };

  return (
    <FullScreenDialog
      open={open}
      onClose={onClose}
      PaperComponent={DialogPaper}
    >
      <div className={classes.box}>
        <div className={classes.close}>
          <IconButton onClick={onClose}>
            <CloseIcon className={classes.closeIcon} />
          </IconButton>
        </div>
      </div>
      <div className={classes.cardContainer}>
        <Typography variant="h2" className={classes.title} gutterBottom>
          Select Card
        </Typography>
        <div className={classes.root}>
          <ImageList cols={1} rowHeight="auto">
            {renderCards()}
          </ImageList>
        </div>
      </div>
    </FullScreenDialog>
  );
}
